/** Demo contracts: clients, pricing type, delivery attribution (REL-021). */

export type PricingType =
  | "FFP"
  | "FFP_EPA"
  | "T&M"
  | "LH"
  | "CPFF"
  | "CPIF"
  | "CPAF"
  | "FPI"
  | "IDIQ_ORDER"
  | "OTHER";

export type ClientOrgNode = {
  id: string;
  name: string;
  parentId: string | null;
  sector: "federal" | "state_local" | "commercial" | "nonprofit" | "other";
};

export type ContractClientLink = {
  clientOrgId: string;
  isPrimary: boolean;
};

export type DemoContract = {
  id: string;
  internalVvCcc: string;
  customerContractNumber: string;
  title: string;
  clients: ContractClientLink[];
  defaultPricingType: PricingType;
  clinPricing: { clin: string; pricingType: PricingType }[];
  period: string;
  pmId: string;
  directorId: string;
  vpId: string;
  businessUnitId: string;
  directorOverridden: boolean;
  vpOverridden: boolean;
  buOverridden: boolean;
  overrideReason?: string;
  award: number;
  ceiling: number;
  funded: number;
  /** Stub portfolio metrics */
  revenueActual: number;
  revenueProj: number;
  directCostActual: number;
  directCostProj: number;
  burdenActual: number;
  burdenProj: number;
};

export const CLIENT_ORG_TREE: ClientOrgNode[] = [
  { id: "cli-fed", name: "Federal Government", parentId: null, sector: "federal" },
  { id: "cli-dod", name: "DoD", parentId: "cli-fed", sector: "federal" },
  { id: "cli-army", name: "Army", parentId: "cli-dod", sector: "federal" },
  { id: "cli-asa", name: "ASA FM&C", parentId: "cli-army", sector: "federal" },
  { id: "cli-noaa", name: "NOAA", parentId: "cli-fed", sector: "federal" },
  { id: "cli-usace", name: "USACE", parentId: "cli-fed", sector: "federal" },
  { id: "cli-gsa", name: "GSA", parentId: "cli-fed", sector: "federal" },
  { id: "cli-comm", name: "Commercial", parentId: null, sector: "commercial" },
];

export function clientName(id: string): string {
  return CLIENT_ORG_TREE.find((c) => c.id === id)?.name ?? id;
}

export function clientPath(id: string): string {
  const parts: string[] = [];
  let cur: ClientOrgNode | undefined = CLIENT_ORG_TREE.find((c) => c.id === id);
  while (cur) {
    parts.unshift(cur.name);
    cur = cur.parentId
      ? CLIENT_ORG_TREE.find((c) => c.id === cur!.parentId)
      : undefined;
  }
  return parts.join(" → ");
}

export const SEED_CONTRACTS: DemoContract[] = [
  {
    id: "con-noaa",
    internalVvCcc: "01.014",
    customerContractNumber: "1332KP24C0001",
    title: "NOAA Coastal Sensors",
    clients: [{ clientOrgId: "cli-noaa", isPrimary: true }],
    defaultPricingType: "CPFF",
    clinPricing: [
      { clin: "0001", pricingType: "CPFF" },
      { clin: "0002", pricingType: "T&M" },
    ],
    period: "000",
    pmId: "emp-ortiz",
    directorId: "emp-dir-fc",
    vpId: "emp-vp-del",
    businessUnitId: "ou-bu-fedciv",
    directorOverridden: false,
    vpOverridden: false,
    buOverridden: false,
    award: 2450000,
    ceiling: 2600000,
    funded: 1800000,
    revenueActual: 920000,
    revenueProj: 2100000,
    directCostActual: 610000,
    directCostProj: 1400000,
    burdenActual: 120000,
    burdenProj: 280000,
  },
  {
    id: "con-army",
    internalVvCcc: "01.018",
    customerContractNumber: "W91CRB-24-C-0042",
    title: "ASA FM&C analytics support",
    clients: [
      { clientOrgId: "cli-asa", isPrimary: true },
      { clientOrgId: "cli-army", isPrimary: false },
    ],
    defaultPricingType: "FFP",
    clinPricing: [{ clin: "0001", pricingType: "FFP" }],
    period: "000",
    pmId: "emp-ortiz",
    directorId: "emp-dir-dod",
    vpId: "emp-vp-del",
    businessUnitId: "ou-bu-dod",
    directorOverridden: true,
    vpOverridden: false,
    buOverridden: true,
    overrideReason: "PM transfer pending — credit stays with DoD BU",
    award: 1100000,
    ceiling: 1100000,
    funded: 550000,
    revenueActual: 280000,
    revenueProj: 980000,
    directCostActual: 190000,
    directCostProj: 620000,
    burdenActual: 40000,
    burdenProj: 140000,
  },
  {
    id: "con-usace",
    internalVvCcc: "01.015",
    customerContractNumber: "W912HQ24F0188",
    title: "USACE Hydrology Support",
    clients: [{ clientOrgId: "cli-usace", isPrimary: true }],
    defaultPricingType: "T&M",
    clinPricing: [{ clin: "0001", pricingType: "T&M" }],
    period: "001",
    pmId: "emp-ortiz",
    directorId: "emp-dir-fc",
    vpId: "emp-vp-del",
    businessUnitId: "ou-bu-fedciv",
    directorOverridden: false,
    vpOverridden: false,
    buOverridden: false,
    award: 980000,
    ceiling: 1200000,
    funded: 700000,
    revenueActual: 410000,
    revenueProj: 900000,
    directCostActual: 290000,
    directCostProj: 610000,
    burdenActual: 55000,
    burdenProj: 120000,
  },
];

export function gm(revenue: number, direct: number) {
  const dollars = revenue - direct;
  const pct = revenue ? (dollars / revenue) * 100 : 0;
  return { dollars, pct };
}

export function op(revenue: number, direct: number, burden: number) {
  const dollars = revenue - direct - burden;
  const pct = revenue ? (dollars / revenue) * 100 : 0;
  return { dollars, pct };
}
